import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Activity, Clock, ChevronDown, ChevronUp, Trophy,
  Target, Zap, Users, TrendingUp, RefreshCw, ExternalLink
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * LiveStatsPanel - Displays live game statistics from multiple providers
 * Supports: StatMoose, PrestoSports, Sidearm
 * Home team on LEFT, Away team on RIGHT
 */
export const LiveStatsPanel = ({ statsUrl, statsProvider = "auto", isLive = true, homeTeam, awayTeam, sport: propSport }) => {
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [detectedProvider, setDetectedProvider] = useState(null);

  // Detect provider from URL
  const detectProvider = useCallback((url) => {
    if (!url) return null;
    const urlLower = url.toLowerCase();
    
    if (urlLower.includes('statmoose.com')) return 'statmoose';
    if (urlLower.includes('prestosports.com') || urlLower.includes('prestostat')) return 'presto';
    if (urlLower.includes('sidearm') || urlLower.match(/[a-z]+sports\.com/)) return 'sidearm';
    if (urlLower.includes('.xml')) return 'presto'; // XML files are typically Presto
    
    return statsProvider !== 'auto' ? statsProvider : 'statmoose'; // Default to StatMoose
  }, [statsProvider]);

  // Extract StatMoose share code from URL
  const getStatMooseCode = useCallback((url) => {
    if (!url) return null;
    const match = url.match(/\/live\/([a-zA-Z0-9]+)/) || url.match(/^([a-zA-Z0-9]+)$/);
    return match ? match[1] : null;
  }, []);

  // Fetch stats based on provider
  const fetchStats = useCallback(async () => {
    if (!statsUrl) {
      setError("No stats URL provided");
      setLoading(false);
      return;
    }

    const provider = detectProvider(statsUrl);
    setDetectedProvider(provider);

    try {
      let data = null;

      if (provider === 'statmoose') {
        // StatMoose - Route through our backend proxy (avoids CORS/firewall blocks)
        const shareCode = getStatMooseCode(statsUrl);
        if (!shareCode) {
          setError("Invalid StatMoose URL");
          setLoading(false);
          return;
        }
        const response = await fetch(`${API}/broadcasts/statmoose/proxy/${shareCode}`);
        if (!response.ok) throw new Error("Failed to fetch StatMoose stats");
        data = await response.json();
        if (data.error) throw new Error(data.error);
        data._provider = 'statmoose';
        
      } else if (provider === 'presto') {
        // PrestoSports - Use our backend to parse and fetch
        try {
          // First, try to parse the URL to get event details
          const parseResponse = await fetch(`${API}/livestats/prestosports/parse?url=${encodeURIComponent(statsUrl)}`, {
            method: 'POST'
          });
          
          if (parseResponse.ok) {
            const parseData = await parseResponse.json();
            console.log("[LiveStats] Parse response:", parseData);
            
            // Check which method was used to parse - support both javascript_config and cached_credentials
            if ((parseData.method === 'javascript_config' || parseData.method === 'cached_credentials') && parseData.event_id && parseData.event_hash) {
              // Construct feed URL with proper encoding
              const feedUrl = `${API}/livestats/prestosports/${parseData.event_id}?base_url=${encodeURIComponent(parseData.base_url)}&event_hash=${encodeURIComponent(parseData.event_hash)}`;
              console.log("[LiveStats] Fetching stats from:", feedUrl);
              const statsResponse = await fetch(feedUrl);
              console.log("[LiveStats] Stats response status:", statsResponse.status);
              if (statsResponse.ok) {
                data = await statsResponse.json();
                data._provider = 'presto';
                console.log("[LiveStats] Stats data loaded:", data.score);
              } else {
                console.log("[LiveStats] Stats fetch failed:", await statsResponse.text());
              }
            } else if (parseData.method === 'html_scrape' && parseData.inline_stats) {
              // Direct HTML scrape: use inline stats
              data = {
                ...parseData.inline_stats,
                _provider: 'presto'
              };
            } else if (parseData.method === 'iframe_redirect' && parseData.redirect_url) {
              // Try fetching the iframe URL
              const iframeParseResponse = await fetch(`${API}/livestats/prestosports/parse?url=${encodeURIComponent(parseData.redirect_url)}`, {
                method: 'POST'
              });
              if (iframeParseResponse.ok) {
                const iframeData = await iframeParseResponse.json();
                if (iframeData.feed_url) {
                  const statsResponse = await fetch(`${API}${iframeData.feed_url}`);
                  if (statsResponse.ok) {
                    data = await statsResponse.json();
                    data._provider = 'presto';
                  }
                } else if (iframeData.inline_stats) {
                  data = {
                    ...iframeData.inline_stats,
                    _provider: 'presto'
                  };
                }
              }
            }
          }
        } catch (e) {
          console.log("Presto parse failed, trying direct fetch:", e);
        }
        
        // If parsing failed, try to display as external link only
        if (!data) {
          data = {
            _provider: 'presto',
            _externalOnly: true,
            source: 'PrestoSports'
          };
        }
        
      } else if (provider === 'sidearm') {
        // Sidearm - Use our backend to parse and fetch
        try {
          const parseResponse = await fetch(`${API}/livestats/sidearm/parse?url=${encodeURIComponent(statsUrl)}`, {
            method: 'POST'
          });
          
          if (parseResponse.ok) {
            const parseData = await parseResponse.json();
            const statsResponse = await fetch(`${API}${parseData.score_url}`);
            if (statsResponse.ok) {
              const scoreData = await statsResponse.json();
              data = {
                ...scoreData,
                _provider: 'sidearm',
                source: 'SidearmSports'
              };
            }
          }
        } catch (e) {
          console.log("Sidearm parse failed:", e);
        }
        
        if (!data) {
          data = {
            _provider: 'sidearm',
            _externalOnly: true,
            source: 'SidearmSports'
          };
        }
      }

      if (data) {
        setGameData(data);
        setLastUpdate(new Date());
        setError(null);
      } else {
        setError("Unable to load stats");
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
      setError("Unable to load stats");
    } finally {
      setLoading(false);
    }
  }, [statsUrl, detectProvider, getStatMooseCode]);

  useEffect(() => {
    if (!statsUrl) return;
    
    fetchStats();
    
    // Auto-refresh every 10 seconds for live games
    if (isLive) {
      const interval = setInterval(fetchStats, 10000);
      return () => clearInterval(interval);
    }
  }, [statsUrl, isLive, fetchStats]);

  // Don't render if no URL
  if (!statsUrl) return null;

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-white/10 p-4" data-testid="live-stats-loading">
        <div className="flex items-center justify-center gap-2 text-zinc-500">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading live stats...</span>
        </div>
      </div>
    );
  }

  // If only external link available
  if (gameData?._externalOnly) {
    return (
      <div className="bg-zinc-900/50 border border-white/10 p-4" data-testid="live-stats-external">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-500" />
            <span className="text-white font-semibold">LIVE STATS</span>
          </div>
          <a 
            href={statsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-orange-500 hover:text-orange-400 text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            View Full Stats
          </a>
        </div>
      </div>
    );
  }

  if (error || !gameData) {
    return null; // Silently hide if stats unavailable
  }

  // Render based on provider
  if (gameData._provider === 'statmoose') {
    return <StatMoosePanel gameData={gameData} statsUrl={statsUrl} homeTeam={homeTeam} awayTeam={awayTeam} expanded={expanded} setExpanded={setExpanded} lastUpdate={lastUpdate} broadcastSport={propSport} />;
  }

  if (gameData._provider === 'presto') {
    return <PrestoPanel gameData={gameData} statsUrl={statsUrl} expanded={expanded} setExpanded={setExpanded} lastUpdate={lastUpdate} broadcastSport={propSport} />;
  }

  if (gameData._provider === 'sidearm') {
    return <SidearmPanel gameData={gameData} statsUrl={statsUrl} expanded={expanded} setExpanded={setExpanded} lastUpdate={lastUpdate} broadcastSport={propSport} />;
  }

  return null;
};

// Helper to get period label based on sport and count
const getPeriodLabel = (index, total, sport) => {
  const sportLower = (sport || '').toLowerCase();
  
  // Basketball - typically halves or quarters
  if (sportLower.includes('basketball')) {
    if (total === 2) return `H${index + 1}`; // Halves
    if (total === 4) return `Q${index + 1}`; // Quarters (NBA style)
    return `${index + 1}`; // Generic period
  }
  
  // Football - quarters
  if (sportLower.includes('football')) {
    return `Q${index + 1}`;
  }
  
  // Hockey - periods
  if (sportLower.includes('hockey')) {
    return `P${index + 1}`;
  }
  
  // Soccer - halves
  if (sportLower.includes('soccer')) {
    return `H${index + 1}`;
  }
  
  // Volleyball - sets
  if (sportLower.includes('volleyball')) {
    return `S${index + 1}`;
  }
  
  // Baseball/Softball - innings
  if (sportLower.includes('baseball') || sportLower.includes('softball')) {
    return `${index + 1}`;
  }
  
  // Default: if 2 periods = halves, 4 periods = quarters, otherwise just numbers
  if (total === 2) return `H${index + 1}`;
  if (total === 4) return `Q${index + 1}`;
  return `${index + 1}`;
};

// StatMoose Panel Component
const StatMoosePanel = ({ gameData, statsUrl, homeTeam, awayTeam, expanded, setExpanded, lastUpdate, broadcastSport }) => {
  // Use broadcast sport if provided, otherwise fall back to gameData.sport
  const sport = broadcastSport || gameData.sport || "basketball";
  
  // Match broadcast teams with StatMoose teams
  const matchTeams = () => {
    if (!homeTeam?.abbreviation && !awayTeam?.abbreviation) {
      return { home: 'home', away: 'away' };
    }

    const homeAbbr = (homeTeam?.abbreviation || '').toLowerCase();
    const awayAbbr = (awayTeam?.abbreviation || '').toLowerCase();
    const smHome = (gameData.home_team_name || '').toLowerCase();
    const smAway = (gameData.away_team_name || '').toLowerCase();

    const homeMatchesSMHome = homeAbbr && (smHome.includes(homeAbbr) || homeAbbr.includes(smHome.split(' ').pop()));
    const homeMatchesSMAway = homeAbbr && (smAway.includes(homeAbbr) || homeAbbr.includes(smAway.split(' ').pop()));

    if (homeMatchesSMHome) return { home: 'home', away: 'away' };
    if (homeMatchesSMAway) return { home: 'away', away: 'home' };

    return { home: 'home', away: 'away' };
  };

  const teamMapping = matchTeams();

  const getTeamData = (team) => {
    const smTeam = teamMapping[team];
    return {
      name: team === 'home' 
        ? (homeTeam?.abbreviation || homeTeam?.name || gameData[`${smTeam}_team_name`])
        : (awayTeam?.abbreviation || awayTeam?.name || gameData[`${smTeam}_team_name`]),
      fullName: gameData[`${smTeam}_team_name`],
      score: gameData[`${smTeam}_score`] ?? (gameData.quarter_scores?.[smTeam]?.reduce((a, b) => a + b, 0) || 0),
      quarterScores: gameData.quarter_scores?.[smTeam] || []
    };
  };

  const homeData = getTeamData('home');
  const awayData = getTeamData('away');
  const isComplete = gameData.status === 'final' || gameData.is_complete;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900/50 border border-white/10 overflow-hidden"
      data-testid="live-stats-panel"
    >
      {/* Header */}
      <div className="bg-black/50 px-4 py-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-500" />
            <span className="text-white font-semibold">LIVE STATS</span>
          </div>
          <div className="flex items-center gap-3">
            <a 
              href={statsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-orange-500 hover:text-orange-400 flex items-center gap-1"
            >
              View Full Stats <ExternalLink className="w-3 h-3" />
            </a>
            {lastUpdate && (
              <span className="text-xs text-zinc-500">
                Updated {lastUpdate.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Score Display */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          {/* Home Team */}
          <div className="flex-1 text-center">
            <div className="text-lg font-bold text-white">{homeData.name}</div>
            <div className="text-4xl font-bold text-white">{homeData.score}</div>
          </div>

          {/* Status */}
          <div className="px-4 text-center">
            <div className={`text-sm font-bold ${isComplete ? 'text-green-500' : 'text-red-500'}`}>
              {isComplete ? 'FINAL' : 'LIVE'}
            </div>
            {!isComplete && gameData.period && (
              <div className="text-xs text-zinc-400">
                {gameData.period} - {gameData.clock || ''}
              </div>
            )}
          </div>

          {/* Away Team */}
          <div className="flex-1 text-center">
            <div className="text-lg font-bold text-white">{awayData.name}</div>
            <div className="text-4xl font-bold text-white">{awayData.score}</div>
          </div>
        </div>

        {/* Quarter/Period Scores */}
        {(homeData.quarterScores.length > 0 || awayData.quarterScores.length > 0) && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-zinc-500">
                  <th className="text-left py-1">Team</th>
                  {homeData.quarterScores.map((_, i) => (
                    <th key={i} className="text-center py-1 w-10">
                      {getPeriodLabel(i, homeData.quarterScores.length, sport)}
                    </th>
                  ))}
                  <th className="text-center py-1 w-12">T</th>
                </tr>
              </thead>
              <tbody className="text-white">
                <tr>
                  <td className="py-1">{homeData.name}</td>
                  {homeData.quarterScores.map((score, i) => (
                    <td key={i} className="text-center py-1">{score}</td>
                  ))}
                  <td className="text-center py-1 font-bold">{homeData.score}</td>
                </tr>
                <tr>
                  <td className="py-1">{awayData.name}</td>
                  {awayData.quarterScores.map((score, i) => (
                    <td key={i} className="text-center py-1">{score}</td>
                  ))}
                  <td className="text-center py-1 font-bold">{awayData.score}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* More Stats Button */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full mt-4 flex items-center justify-center gap-2 text-zinc-400 hover:text-white text-sm py-2"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {expanded ? 'Less' : 'More'}
        </button>
      </div>
    </motion.div>
  );
};

// PrestoSports Panel Component
const PrestoPanel = ({ gameData, statsUrl, expanded, setExpanded, lastUpdate, broadcastSport }) => {
  const sport = broadcastSport || gameData.sport || "basketball";
  const score = gameData.score || {};
  const isComplete = score.is_final;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900/50 border border-white/10 overflow-hidden"
      data-testid="live-stats-panel"
    >
      {/* Header */}
      <div className="bg-black/50 px-4 py-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-500" />
            <span className="text-white font-semibold">LIVE STATS</span>
          </div>
          <div className="flex items-center gap-3">
            <a 
              href={statsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-orange-500 hover:text-orange-400 flex items-center gap-1"
            >
              View Full Stats <ExternalLink className="w-3 h-3" />
            </a>
            {lastUpdate && (
              <span className="text-xs text-zinc-500">
                Updated {lastUpdate.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Score Display */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          {/* Home Team */}
          <div className="flex-1 text-center">
            <div className="text-lg font-bold text-white">{score.home_team}</div>
            <div className="text-4xl font-bold text-white">{score.home_score}</div>
            {score.home_record && <div className="text-xs text-zinc-500">{score.home_record}</div>}
          </div>

          {/* Status */}
          <div className="px-4 text-center">
            <div className={`text-sm font-bold ${isComplete ? 'text-green-500' : 'text-red-500'}`}>
              {score.period || (isComplete ? 'FINAL' : 'LIVE')}
            </div>
            {!isComplete && score.clock && (
              <div className="text-xs text-zinc-400">{score.clock}</div>
            )}
          </div>

          {/* Away Team */}
          <div className="flex-1 text-center">
            <div className="text-lg font-bold text-white">{score.away_team}</div>
            <div className="text-4xl font-bold text-white">{score.away_score}</div>
            {score.away_record && <div className="text-xs text-zinc-500">{score.away_record}</div>}
          </div>
        </div>

        {/* Team Stats Comparison */}
        {expanded && gameData.home_team_stats && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <h4 className="text-xs font-bold text-zinc-500 uppercase mb-3">Team Stats</h4>
            <div className="space-y-2">
              <StatRow label="FG %" home={gameData.home_team_stats.fg_pct} away={gameData.away_team_stats.fg_pct} />
              <StatRow label="3PT %" home={gameData.home_team_stats.fg3_pct} away={gameData.away_team_stats.fg3_pct} />
              <StatRow label="Rebounds" home={gameData.home_team_stats.rebounds} away={gameData.away_team_stats.rebounds} />
              <StatRow label="Assists" home={gameData.home_team_stats.assists} away={gameData.away_team_stats.assists} />
              <StatRow label="Turnovers" home={gameData.home_team_stats.turnovers} away={gameData.away_team_stats.turnovers} isLowerBetter />
            </div>
          </div>
        )}

        {/* More Stats Button */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full mt-4 flex items-center justify-center gap-2 text-zinc-400 hover:text-white text-sm py-2"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {expanded ? 'Less' : 'More'}
        </button>
      </div>
    </motion.div>
  );
};

// Sidearm Panel Component
const SidearmPanel = ({ gameData, statsUrl, expanded, setExpanded, lastUpdate, broadcastSport }) => {
  const sport = broadcastSport || gameData.sport || "basketball";
  const isComplete = gameData.is_final;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900/50 border border-white/10 overflow-hidden"
      data-testid="live-stats-panel"
    >
      {/* Header */}
      <div className="bg-black/50 px-4 py-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-500" />
            <span className="text-white font-semibold">LIVE STATS</span>
          </div>
          <div className="flex items-center gap-3">
            <a 
              href={statsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-orange-500 hover:text-orange-400 flex items-center gap-1"
            >
              Full Stats <ExternalLink className="w-3 h-3" />
            </a>
            {lastUpdate && (
              <span className="text-xs text-zinc-500">
                Updated {lastUpdate.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Score Display */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          {/* Home Team */}
          <div className="flex-1 text-center">
            <div className="text-lg font-bold text-white">{gameData.home_team}</div>
            <div className="text-4xl font-bold text-white">{gameData.home_score}</div>
          </div>

          {/* Status */}
          <div className="px-4 text-center">
            <div className={`text-sm font-bold ${isComplete ? 'text-green-500' : 'text-red-500'}`}>
              {gameData.period || (isComplete ? 'FINAL' : 'LIVE')}
            </div>
            {!isComplete && gameData.clock && (
              <div className="text-xs text-zinc-400">{gameData.clock}</div>
            )}
          </div>

          {/* Away Team */}
          <div className="flex-1 text-center">
            <div className="text-lg font-bold text-white">{gameData.away_team}</div>
            <div className="text-4xl font-bold text-white">{gameData.away_score}</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Stat Row Component for comparison
const StatRow = ({ label, home, away, isLowerBetter = false }) => {
  const homeValue = parseFloat(home) || 0;
  const awayValue = parseFloat(away) || 0;
  
  const homeWinning = isLowerBetter ? homeValue < awayValue : homeValue > awayValue;
  const awayWinning = isLowerBetter ? awayValue < homeValue : awayValue > homeValue;

  return (
    <div className="flex items-center justify-between text-sm">
      <span className={`w-16 text-right ${homeWinning ? 'text-green-400 font-bold' : 'text-zinc-400'}`}>
        {typeof home === 'number' ? home.toFixed(1) : home}
      </span>
      <span className="flex-1 text-center text-zinc-500 text-xs">{label}</span>
      <span className={`w-16 text-left ${awayWinning ? 'text-green-400 font-bold' : 'text-zinc-400'}`}>
        {typeof away === 'number' ? away.toFixed(1) : away}
      </span>
    </div>
  );
};

export default LiveStatsPanel;
